/**
 * HighlightRenderer — Draws a pulsing outline around a page element
 * using a DOM overlay (not PixiJS, since page elements are outside the canvas).
 */

export class HighlightRenderer {
  private activeHighlights: HTMLDivElement[] = []

  /** Highlight a DOM element with a pulsing outline for the given duration. */
  highlight(el: HTMLElement, durationMs = 3000): void {
    const rect = el.getBoundingClientRect()

    const overlay = document.createElement('div')
    Object.assign(overlay.style, {
      position: 'fixed',
      left: `${rect.left - 4}px`,
      top: `${rect.top - 4}px`,
      width: `${rect.width + 8}px`,
      height: `${rect.height + 8}px`,
      border: '3px solid #4a6fa5',
      borderRadius: '8px',
      boxShadow: '0 0 12px rgba(74, 111, 165, 0.5)',
      pointerEvents: 'none',
      zIndex: '2147483645',
      animation: 'hs-highlight-pulse 1.2s ease-in-out infinite',
      transition: 'opacity 0.3s ease',
    })

    // Inject keyframes if not already present
    this.ensureKeyframes()

    document.body.appendChild(overlay)
    this.activeHighlights.push(overlay)

    // Update position on scroll
    const onScroll = () => {
      const newRect = el.getBoundingClientRect()
      overlay.style.left = `${newRect.left - 4}px`
      overlay.style.top = `${newRect.top - 4}px`
      overlay.style.width = `${newRect.width + 8}px`
      overlay.style.height = `${newRect.height + 8}px`
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    // Auto-remove after duration
    setTimeout(() => {
      overlay.style.opacity = '0'
      setTimeout(() => {
        overlay.remove()
        window.removeEventListener('scroll', onScroll)
        this.activeHighlights = this.activeHighlights.filter(h => h !== overlay)
      }, 300)
    }, durationMs)
  }

  /** Remove all active highlights. */
  clearAll(): void {
    for (const h of this.activeHighlights) {
      h.remove()
    }
    this.activeHighlights = []
  }

  private ensureKeyframes(): void {
    if (document.getElementById('hs-highlight-keyframes')) return

    const style = document.createElement('style')
    style.id = 'hs-highlight-keyframes'
    style.textContent = `
      @keyframes hs-highlight-pulse {
        0%, 100% { box-shadow: 0 0 8px rgba(74, 111, 165, 0.4); border-color: #4a6fa5; }
        50% { box-shadow: 0 0 20px rgba(74, 111, 165, 0.8); border-color: #6b8fc5; }
      }
    `
    document.head.appendChild(style)
  }
}
